// Tracks per-message expiry deadlines and wipes plaintext when due.
//
// Ported from MessageExpiryScheduler.swift. Two layers, deliberately split so
// tests can drive the inner one without real time:
//
//   - tick(now) is a pure-logic function: given the wall-clock time, it pops
//     every entry whose expiresAt <= now, fires the wipe callback, and
//     persists the trimmed list.  A test can call tick() with arbitrary Dates
//     to verify the wipe fires at the right moment.
//
//   - start() / stop() install a 1-second setInterval that calls tick(now).
//     This is the production driver; tests never touch it.
//
// Persistence: tracked entries live in a single IndexedDB object store so a
// page reload doesn't lose pending wipes.  On next load, the very first tick
// will catch up any messages whose deadlines passed while the page was closed.
//
// NOTE on start()/stop(): actual setTimeout/setInterval wiring is out of scope
// for the pure-logic core — callers invoke tick(at) directly for scheduling.
// start() / stop() are stubs that can be wired to setInterval by the caller.

const DEFAULT_DB_NAME = "kaether-expiry-scheduler";
const STORE_NAME = "entries";
const DB_VERSION = 1;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ExpiryEntry {
  readonly messageId: string;
  readonly channelId: string;
  readonly encryptedContentHash: string; // for purging from PlaintextStore
  readonly expiresAtMs: number;          // ms since epoch
}

export interface MessageWiper {
  wipe(messageId: string, channelId: string, encryptedContentHash: string): void;
}

/**
 * Default wiper. In production this would purge PlaintextStore and post a
 * custom event so visible message bubbles can re-render as "expired".
 *
 * The event detail mirrors iOS DefaultMessageWiper's NotificationCenter post.
 */
export class DefaultMessageWiper implements MessageWiper {
  static readonly DID_EXPIRE_EVENT = "kaether:disappearingMessage:didExpire";

  wipe(messageId: string, channelId: string, encryptedContentHash: string): void {
    // TODO(web): call PlaintextStore.purge(encryptedContentHash) once
    // PlaintextStore exposes a synchronous/async purge by hash.
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(DefaultMessageWiper.DID_EXPIRE_EVENT, {
          detail: { messageId, channelId, encryptedContentHash },
        }),
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  const arr = new Uint8Array(hash);
  let hex = "";
  for (let i = 0; i < arr.length; i++) {
    hex += arr[i].toString(16).padStart(2, "0");
  }
  return hex;
}

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME, { keyPath: "messageId" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Scheduler
// ---------------------------------------------------------------------------

export class MessageExpiryScheduler {
  private readonly dbName: string;
  private readonly wiper: MessageWiper;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(dbName: string = DEFAULT_DB_NAME, wiper: MessageWiper = new DefaultMessageWiper()) {
    this.dbName = dbName;
    this.wiper = wiper;
  }

  // -------------------------------------------------------------------------
  // Content hash (mirrors iOS SHA256 helper)

  static async contentHash(s: string): Promise<string> {
    return sha256Hex(s);
  }

  // -------------------------------------------------------------------------
  // Tracking

  /**
   * Register a message for expiry.  If `expiresAt` is already in the past,
   * the wipe fires synchronously (await-safe) — useful for catching up after
   * a long page-close gap without waiting for the next tick.
   */
  async track(
    messageId: string,
    channelId: string,
    encryptedContent: string,
    expiresAt: Date,
    now: Date = new Date(),
  ): Promise<void> {
    const hash = await sha256Hex(encryptedContent);
    if (expiresAt.getTime() <= now.getTime()) {
      this.wiper.wipe(messageId, channelId, hash);
      return;
    }
    const db = await openDb(this.dbName);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      const store = tx.objectStore(STORE_NAME);
      // Replace any existing entry for the same messageId (re-tracks update
      // the deadline rather than accumulating duplicates).
      const entry: ExpiryEntry = {
        messageId,
        channelId,
        encryptedContentHash: hash,
        expiresAtMs: expiresAt.getTime(),
      };
      store.put(entry); // put = upsert by keyPath "messageId"
    });
    db.close();
  }

  /** Cancel a previously-tracked message without firing the wipe. */
  async cancel(messageId: string): Promise<void> {
    const db = await openDb(this.dbName);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE_NAME).delete(messageId);
    });
    db.close();
  }

  async pendingEntries(): Promise<ExpiryEntry[]> {
    return this._readEntries();
  }

  // -------------------------------------------------------------------------
  // Tick (deterministic, for tests AND production)

  /**
   * Given the current wall-clock time, pops every entry whose deadline has
   * passed, fires the wipe, persists the trimmed list, and returns the set of
   * wiped entries.
   */
  async tick(now: Date = new Date()): Promise<ExpiryEntry[]> {
    const all = await this._readEntries();
    const nowMs = now.getTime();
    const due = all.filter((e) => e.expiresAtMs <= nowMs);
    if (due.length === 0) return [];

    const remaining = all.filter((e) => e.expiresAtMs > nowMs);
    await this._writeEntries(remaining);

    for (const entry of due) {
      this.wiper.wipe(entry.messageId, entry.channelId, entry.encryptedContentHash);
    }
    return due;
  }

  // -------------------------------------------------------------------------
  // Production timer (wiring only — not under test)

  /**
   * Install a 1-second interval that calls tick(now). Idempotent.
   * Matches iOS MessageExpiryScheduler.start().
   */
  start(): void {
    if (this.intervalId !== null) return;
    this.intervalId = setInterval(() => {
      void this.tick(new Date());
    }, 1000);
  }

  /** Cancel the production interval. Matches iOS MessageExpiryScheduler.stop(). */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // -------------------------------------------------------------------------
  // Storage

  private async _readEntries(): Promise<ExpiryEntry[]> {
    const db = await openDb(this.dbName);
    return new Promise<ExpiryEntry[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).getAll();
      tx.oncomplete = () => {
        db.close();
        resolve((req.result as ExpiryEntry[]) ?? []);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  }

  private async _writeEntries(entries: ExpiryEntry[]): Promise<void> {
    const db = await openDb(this.dbName);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      const store = tx.objectStore(STORE_NAME);
      store.clear();
      for (const e of entries) {
        store.put(e);
      }
    });
    db.close();
  }
}
